import { Box, Grid, Typography } from "@mui/material";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import { ReactComponent as EditIcon } from "../../../../common/icons/editGray.svg";
import { ReactComponent as NoPricingIcon } from "../../../../common/icons/noPricing.svg";
import WhiteCard from "../../WhiteCard";
import { OpenModalParams } from "../ContractsTabContent";
import { ContractsTabModalTypes } from "../constants";
import {
  formatDateStringForUI,
  getMostRecentPastDateFromArray,
} from "../helpers/commonUtils";
import { EditPricingButton, PricingListSubHeader } from "../styles";

interface Props {
  pricingExists: boolean;
  pricings: any[];
  pricingsObj: any;
  existingPricingValidFromDates: any;
  handleOpenModal: (params: OpenModalParams) => void;
  canEdit: boolean;
  canRead: boolean;
}

const PricingList = ({
  pricingExists,
  pricings,
  pricingsObj,
  existingPricingValidFromDates,
  handleOpenModal,
  canEdit,
  canRead,
}: Props) => {
  const { t } = useTranslation("customers");
  const { tes } = useAppSelector((state) => state.localized);

  const currentlyValidPricingDate = getMostRecentPastDateFromArray(
    existingPricingValidFromDates,
  );

  const getPricingListSubHeader = (pricingValidFromDate: string) => {
    if (pricingValidFromDate === currentlyValidPricingDate) {
      return `${formatDateStringForUI(pricingValidFromDate)} (${t(
        "contract.validPricingText",
      )})`;
    }
    return formatDateStringForUI(pricingValidFromDate);
  };

  return (
    <>
      {pricingExists ? (
        pricings.map((pricing) => {
          // Sort pricing tes items (general pricing settings will still always be first)
          const sortedPricingTesList = pricing.pricingTesList.sort(
            (a: any, b: any) => a.tesId.localeCompare(b.tesId),
          );

          return (
            <Fragment key={pricing.pricingValidFromDate}>
              <Grid container sx={{ margin: "0.75rem 1rem" }}>
                <PricingListSubHeader
                  sx={{
                    color: !canRead ? "text.disabled" : "inherit",
                  }}
                >
                  {getPricingListSubHeader(pricing.pricingValidFromDate)}
                </PricingListSubHeader>
                <EditPricingButton
                  accessRightChecks={[canEdit]}
                  onClick={() =>
                    handleOpenModal({
                      type: ContractsTabModalTypes.EditExistingPricing,
                      data: pricing,
                    })
                  }
                >
                  <EditIcon />
                </EditPricingButton>
              </Grid>
              {pricing.pricingGeneralSettings && (
                <Grid item sx={{ paddingLeft: "1rem" }}>
                  <WhiteCard
                    active
                    disabled={!canRead}
                    onClick={() => {
                      if (canRead) {
                        return handleOpenModal({
                          type: ContractsTabModalTypes.PricingGeneralSettings,
                          data: {
                            pricingValidFromDate: pricing.pricingValidFromDate,
                            pricingGeneralSettings:
                              pricing.pricingGeneralSettings,
                            existingPricingObjectFromApi:
                              pricingsObj[pricing.pricingValidFromDate],
                          },
                        });
                      }
                    }}
                    primary={`${t("contract.pricing")} - ${t(
                      "contract.generalPricingShortAlt",
                    )}`}
                  />
                </Grid>
              )}
              {sortedPricingTesList.map((pricingTesItem: any) => (
                <Grid
                  key={`${pricing.pricingValidFromDate}_${pricingTesItem.tesId}`}
                  item
                  sx={{ paddingLeft: "1rem" }}
                >
                  <WhiteCard
                    active
                    disabled={!canRead}
                    onClick={() => {
                      if (canRead) {
                        return handleOpenModal({
                          type: ContractsTabModalTypes.PricingPerTes,
                          data: {
                            pricingValidFromDate: pricing.pricingValidFromDate,
                            existingPricingObjectFromApi:
                              pricingsObj[pricing.pricingValidFromDate],
                            pricingTesItem,
                          },
                        });
                      }
                    }}
                    primary={`${t("contract.pricing")} - ${
                      tes[pricingTesItem.tesId]
                    }`}
                  />
                </Grid>
              ))}
            </Fragment>
          );
        })
      ) : (
        <Box sx={{ marginLeft: "1em", marginTop: "1em", marginBottom: "1em" }}>
          <NoPricingIcon />
          <Typography sx={{ marginTop: "0.5em" }}>
            {t("contract.noPricingGuideText")}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default PricingList;
